import React from 'react';

function TermsConditions() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white font-sans relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-cyan-500 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-purple-500 rounded-full blur-3xl"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-60 h-60 bg-blue-500 rounded-full blur-3xl"></div>
      </div>

      <div className="relative z-10 p-4 sm:p-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-600 bg-clip-text text-transparent mb-4">
            Terms and Conditions
          </h1>
          <p className="text-lg sm:text-xl text-gray-300">Last updated: November 2, 2025</p>
        </div>

        {/* Introduction */}
        <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 border border-white/20 shadow-2xl">
          <p className="text-gray-300 leading-relaxed text-base sm:text-lg">
            Welcome to Fixet! These Terms and Conditions govern your use of our urban services platform.
            By accessing or using our services, you agree to be bound by these terms. Please read them carefully.
          </p>
        </div>

        {/* Terms Sections */}
        <div className="space-y-6 sm:space-y-8">
          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-cyan-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">📋</span> Acceptance of Terms
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>By accessing and using Fixet services, you accept and agree to be bound by these terms:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You must be at least 18 years old to use our services</li>
                <li>You agree to provide accurate and complete information</li>
                <li>You are responsible for maintaining the confidentiality of your account</li>
                <li>You agree to use the platform for lawful purposes only</li>
              </ul>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-green-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">🚗</span> Service Provider Terms
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>If you provide services through our platform:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>You must have valid licenses and certifications for your services</li>
                <li>You agree to maintain service quality and customer satisfaction</li>
                <li>You are responsible for your own taxes and business expenses</li>
                <li>You must comply with all local laws and regulations</li>
                <li>You agree to maintain appropriate insurance coverage</li>
              </ul>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-yellow-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">💰</span> Payment Terms
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>Payment processing and related terms:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>All payments are processed securely through our payment partners</li>
                <li>Service providers receive payments after successful completion</li>
                <li>Platform fees are deducted as per our pricing structure</li>
                <li>Refunds are processed according to our refund policy</li>
                <li>Disputes must be reported within 48 hours of service completion</li>
              </ul>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-purple-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">⚖️</span> User Responsibilities
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>As a user of our platform, you agree to:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Provide accurate information about services needed</li>
                <li>Maintain respectful communication with service providers</li>
                <li>Arrive on time for scheduled services</li>
                <li>Pay for services as agreed upon</li>
                <li>Report any issues or concerns promptly</li>
                <li>Comply with cancellation policies</li>
              </ul>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-blue-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">🛡️</span> Liability & Disclaimers
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p className="font-semibold text-white">Service Quality Disclaimer:</p>
              <p>While we strive to connect you with quality service providers, Fixet does not guarantee the quality of services provided by independent contractors.</p>

              <p className="font-semibold text-white mt-4">Limitation of Liability:</p>
              <p>In no event shall Fixet be liable for any indirect, incidental, special, or consequential damages arising out of your use of our services.</p>

              <p className="font-semibold text-white mt-4">Platform Availability:</p>
              <p>We strive for 99.9% uptime but do not guarantee uninterrupted access to our platform.</p>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-red-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">🚫</span> Prohibited Activities
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p>You agree not to engage in any of the following prohibited activities:</p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>Violating any applicable laws or regulations</li>
                <li>Providing false or misleading information</li>
                <li>Harassing or abusing other users or service providers</li>
                <li>Attempting to circumvent our payment system</li>
                <li>Using the platform for any illegal or unauthorized purpose</li>
                <li>Impersonating another person or entity</li>
              </ul>
            </div>
          </div>

          <div className="backdrop-blur-lg bg-white/10 rounded-2xl sm:rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
            <h2 className="text-orange-400 text-xl sm:text-2xl lg:text-3xl font-bold mb-4 sm:mb-6 flex items-center">
              <span className="mr-2 sm:mr-3 text-lg sm:text-xl">📞</span> Contact & Dispute Resolution
            </h2>
            <div className="space-y-3 sm:space-y-4 text-gray-300 leading-relaxed text-sm sm:text-base">
              <p className="font-semibold text-white">Contact Information:</p>
              <div className="mt-4 p-4 sm:p-6 bg-white/5 rounded-xl sm:rounded-2xl border border-white/10">
                <p>If you have questions about these Terms and Conditions:</p>
                <p className="mt-2 text-sm sm:text-base">📧 legal@fixet.com</p>
                <p className="text-sm sm:text-base">📱 +91-XXXXXXXXXX</p>
                <p className="text-sm sm:text-base">🏢 Fixet Technologies Pvt Ltd, New Delhi, India</p>
              </div>

              <p className="font-semibold text-white mt-4">Dispute Resolution:</p>
              <p>Any disputes arising from these terms shall be resolved through binding arbitration in accordance with Indian law.</p>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 sm:mt-12 p-4 sm:p-6 backdrop-blur-lg bg-white/5 rounded-xl sm:rounded-2xl border border-white/10">
          <p className="text-gray-400 text-sm sm:text-base">
            These Terms and Conditions are effective as of November 2, 2025. We reserve the right to modify these terms at any time.
            Continued use of our services after changes constitutes acceptance of the new terms.
          </p>
        </div>
      </div>
    </div>
  );
}

export default TermsConditions;